import SeoLandingLayout from "@/components/cre/SeoLandingLayout";

const RELATED = [
  { to: "/ai-incident-management", label: "AI incident management" },
  { to: "/aiops-incident-reasoning", label: "AIOps incident reasoning" },
  { to: "/root-cause-analysis-ai", label: "Root cause analysis AI" },
  { to: "/major-incident-management", label: "Major incident management" },
  { to: "/it-downtime-reduction", label: "IT downtime reduction" },
];

const FAQ = [
  {
    q: "How do I try CREAIOps?",
    a: "You can run CREAIOps on an anonymized incident scenario directly in the browser — no signup, no setup, no card. The workspace will produce ranked RCA hypotheses, recommended actions, and a stakeholder draft for review.",
  },
  {
    q: "Do I need to share production data to evaluate CREAIOps?",
    a: "No. Use anonymized incident inputs only. CREAIOps is designed to reason from anonymized context, not raw production logs, secrets, or regulated information.",
  },
  {
    q: "Will CREAIOps make changes to my systems during the walkthrough?",
    a: "No. CREAIOps is advisory only. It does not execute remediation, push configuration, or run scripts. Every recommendation requires explicit human approval.",
  },
  {
    q: "Who should evaluate CREAIOps?",
    a: "Incident managers, SRE leaders, IT operations leaders, problem managers, and service-delivery teams typically lead the evaluation. Senior on-call engineers are usually the first technical validators.",
  },
  {
    q: "Can I save analyses from the walkthrough?",
    a: "Sign in to save incident history, revisit previous analyses, and export reports. The no-login walkthrough is a single anonymized run, with results visible immediately.",
  },
];

export default function Demo() {
  return (
    <SeoLandingLayout
      slug="demo"
      metaTitle="See CREAIOps in Action | AI Incident Reasoning Walkthrough"
      metaDescription="See how CREAIOps reasons through a real-world incident scenario. Try the AI incident reasoning workspace with anonymized inputs — no signup, no setup, no auto-remediation."
      h1="See CREAIOps reason through a real-world incident"
      intro="Run CREAIOps on an anonymized incident scenario directly in your browser. The workspace will dispatch domain reasoning agents, rank likely root causes, generate a stakeholder draft, and produce a human-reviewable runbook — without signup or setup."
      problem={{
        title:
          "Most incident tools demo dashboards. CREAIOps demos reasoning.",
        body:
          "Evaluating an incident platform is hard when the demo focuses on dashboards instead of the moment that matters: how does the tool actually help a senior engineer think faster? CREAIOps inverts the demo — you see live reasoning, ranked hypotheses, and approval-ready artefacts on your scenario.",
        points: [
          "See how multi-agent reasoning produces ranked hypotheses",
          "Review evidence and confidence behind every suggestion",
          "Generate stakeholder and engineering narratives in parallel",
          "Validate human-in-the-loop governance end to end",
        ],
      }}
      howWeHelp={{
        title:
          "An anonymized walkthrough that mirrors your real major-incident bridge",
        body:
          "The walkthrough lets you submit an anonymized incident, watch domain agents reason in parallel, review ranked hypotheses, and approve a runbook — exactly the workflow your team would use in a live sev-1.",
        items: [
          {
            title: "Multi-agent reasoning, in real time",
            desc: "Application, infrastructure, network, security, database, and business-impact agents publish hypotheses you can review.",
          },
          {
            title: "Stakeholder + engineering narratives",
            desc: "Generate aligned updates for leadership and engineering from the same reasoning trail.",
          },
          {
            title: "Human approval gate",
            desc: "Approve the recommended runbook explicitly — exactly as you would during a live incident.",
          },
          {
            title: "No signup, no setup",
            desc: "Run the walkthrough on an anonymized scenario directly in the browser.",
          },
        ],
      }}
      benefits={[
        "Evaluate CREAIOps using your own anonymized incident scenario",
        "Validate governance fit with your change-management process",
        "Compare reasoning quality across domain lenses",
        "Share the experience with peers — including stakeholder and engineering views",
        "Move quickly from evaluation to a pilot with a saved-history account",
      ]}
      useCase={{
        title:
          "A platform engineering team evaluates CREAIOps in a single working session",
        body:
          "An SRE lead submits an anonymized version of a recent incident. Within minutes, the team reviews ranked hypotheses, walks through the approval flow, and aligns on whether CREAIOps fits their major-incident process.",
        steps: [
          "SRE lead opens the CREAIOps walkthrough",
          "Anonymized incident scenario is submitted",
          "Reasoning agents produce ranked hypotheses and safe actions",
          "Stakeholder and engineering drafts are generated",
          "Reviewer approves the recommended runbook",
          "Team decides next step: sign up to save history, or run a pilot",
        ],
      }}
      faq={FAQ}
      related={RELATED}
      ctaPrimary={{ label: "Start the walkthrough", to: "/preview" }}
      ctaSecondary={{ label: "Sign in to save history", to: "/login" }}
    />
  );
}
